import { existsFilter } from '@iotv/core-services';
import { Device, DeviceClassName, GatewayClassName, isValidDeviceObject, UserClassName, } from '@iotv/datamodel-core';
import { ErrDataArrayReducer, PathDirection, } from '@iotv/iotv-v3-types';
import { v4 } from 'uuid';
import { displayMessage } from '../../../../components/messages/displayMessage';
import AppDao from '../../../../data/AppDao';
import { ConsumerDao } from '../../../../data/ConsumerDAO';
import { DeviceDAO, UserDAO } from '../../../../data/SystemManagementDAO';
import { GatewayDAO } from '../../../../data/SystemManagementDAO/networkFns';
import { link } from '../../../../data/daoFunctions/daoFunctions';
import { addObjectsToState } from '../../../../redux/actions/AppActionFunctions/AppActions';
import { setUserMessage } from '../../../../redux/actions/AppActionFunctions/userMessages';
import MainStore from '../../../../redux/stores/MainStore';
import { AdjacentType, Severity } from '../../../../types/AppTypes';
const debug = process.env.REACT_APP_DEBUG && false;
/** Function that handles adding a new device, including adminability, etc */
export const addNewDevice = async ({ device, deviceGroup, owningCustomer, contextUser }) => {
    const addDevice_res = { err: null, data: null };
    const instance = new Device(device);
    const isValidDevice = isValidDeviceObject(device) && instance.isLoRaLNSCompatible();
    if (!isValidDevice) {
        const idNotEui = device.id !== device.eui;
        const invalidKeys = instance.getInvalidLNSAttributes();
        addDevice_res.err = new Error(`Rejecting create device request: ${idNotEui ? 'id not equal to eui' : ''} ${invalidKeys.length > 0 ? invalidKeys.join(', ') : ''}`);
    }
    else {
        const { err, data } = await DeviceDAO.addDevice({
            device,
            deviceGroup,
            contextUser,
            contextCustomer: owningCustomer,
        });
        addDevice_res.data = data;
        addDevice_res.err = err;
    }
    return addDevice_res;
};
export const addNewGateway = async ({ gateway, contextUser, contextGroup, contextCustomer }) => {
    const apexCustomersRes = await ConsumerDao.getNamedApexItems({ type: 'Customer' });
    const res = { err: null, data: null };
    const apexCustomers = apexCustomersRes.data;
    for (const apexCustomer of apexCustomers) {
        const assignmentRes = await GatewayDAO.assignGatewayToCustomer({
            contextCustomer: apexCustomer,
            assignmentCustomer: contextCustomer,
            assignmentCustomerGroup: contextGroup,
            contextUser,
            gateway,
        });
        if (assignmentRes.data) {
            const { assignmentCustomer, assignmentCustomerGatewayEdge, assignmentCustomerGroup, assignmentCustomerGroupEdge, contextCustomer, gateway } = assignmentRes.data;
            res.data = gateway;
            MainStore.dispatch(addObjectsToState([assignmentCustomerGatewayEdge, assignmentCustomerGroupEdge, gateway].filter(existsFilter)));
            dispatchSuccessMessage(`Assigned from ${apexCustomer.name} to ${contextCustomer.name}`);
            if (contextGroup && !assignmentCustomerGroupEdge) {
                dispatchErrorMessage(`Error Linking ${gateway.type} ${gateway.name} to Group ${contextGroup.name}`);
            }
            break;
        }
        else if (assignmentRes.err) {
            const { err } = assignmentRes;
            const msg = res.err?.message ? `${res.err.message}, ${err.message}` : err.message;
            res.err = new Error(msg);
            dispatchErrorMessage(`Error in assignment from ${apexCustomer.name}: ${assignmentRes.err.message}`);
        }
    }
    return res;
};
/**
 * Derived from WXC attempt to generalize adding Devices and Gateways to inventory
 * Further modified by ADH to enable simple device saving - Jan 23 2025
 * @deprecated ADH - Please use {@link addNewDevice | `addNewDevice()`} or {@link addNewGateway | `addNewGateway()`}
 */
export const saveSysObjects = async ({ contextUser, contextCustomer, contextGroup, systemObject, navigate }) => {
    const type = systemObject.type;
    systemObject = {
        ...systemObject,
        id: systemObject.eui,
        pk: `${type}:${systemObject.eui}`,
        sk: `${type}:${systemObject.eui}`,
    };
    if (!contextCustomer) {
        return { err: new Error('Save Sys Objects: No customer!'), data: null };
    }
    let addDeviceFn = null;
    let addGatewayFn = null;
    const notSupportedFn = () => Promise.resolve({
        err: new Error(`${systemObject.type} not supported`),
        data: null,
    });
    /** Simple Add Device */
    addDeviceFn = async () => {
        const res = await addNewDevice({
            device: systemObject,
            deviceGroup: contextGroup,
            contextUser,
            owningCustomer: contextCustomer,
        });
        if (res.err) {
            return { err: res.err, data: null };
        }
        return { err: null, data: res?.data?.device ?? null };
    };
    addGatewayFn = async () => {
        const res = await addNewGateway({
            gateway: systemObject,
            contextCustomer,
            contextGroup: contextGroup,
            contextUser,
        });
        return res;
    };
    const addSystemObjectMap = {
        Device: addDeviceFn,
        Gateway: addGatewayFn,
    };
    const addSystemObjectFn = addSystemObjectMap[systemObject.type] ?? notSupportedFn;
    return addSystemObjectFn();
};
/** This is derived from EDM attempts in ...withAddMenu and Listerlzier
 * @deprecated - Only used in old version of detailCard. Use UserDAO functions.
 * */
export const saveUser = async ({ contextCustomer, contextUser, makeAdmin, newUserObject, addToGroups = [], contextObject, userFunctions, }) => {
    let adjacentFilter = undefined;
    const res = { err: null, data: null };
    const objectsToAddToState = [];
    debug &&
        console.log(`saveUser has `, {
            contextCustomer,
            contextUser,
            newUserObject,
            addToGroups,
            contextObject,
            userFunctions,
        });
    if (contextObject?.type === 'Group') {
        if (contextObject.supportedType === 'User') {
            adjacentFilter = {
                adjacentType: AdjacentType.CHILD,
                edgeFilter: {
                    edgeTypeId: 'group_has_' + contextObject.supportedType.toLowerCase(),
                },
                objectType: contextObject.supportedType,
                otherObjectType: undefined,
            };
        }
        else {
            displayMessage(`Not adding ${newUserObject.name} to ${contextObject.name} because ${contextObject.name} is not a user group`, 'MessageBar', userFunctions, Severity.warning);
        }
    }
    try {
        if (contextUser) {
            const { temporaryPassword, Admin, ...user } = newUserObject;
            if (user.type === 'User') {
                if (temporaryPassword) {
                    /** The back end (system-provisioning) adds the user group (role == cognito group) if no roles are selected */
                    //if (!addToGroups?.includes("User")) {
                    //  addToGroups.push('User')
                    //}
                    if (user.phone_number && !user.phoneNumber)
                        user.phoneNumber = user.phoneNumber ?? user.phone_number;
                    /** THE USER VARIANT FORM DOES NOT LET MAKEADMIN BE SET */
                    const addUserRequest = {
                        makeAdmin,
                        user: user,
                        temporaryPassword,
                        contextUser: contextUser,
                        contextCustomer,
                        addToGroups,
                    };
                    user.username = user.username?.toLowerCase();
                    user['create_time'] = Date.now();
                    const response = await UserDAO.addUser(addUserRequest);
                    if (response.data) {
                        const newUser = response.data;
                        /** add the new user to list to be put into app state */
                        objectsToAddToState.push(newUser);
                        res.data = newUser;
                        if (contextObject && newUser) {
                            /** link the new user to context object ( usually group ) */
                            debug &&
                                console.log('Save user, savaAndLink', {
                                    source: contextObject,
                                    target: response.data,
                                    adjacentFilter,
                                    edgeAttributes: {},
                                });
                            const linkRes = await link(contextObject, newUser, adjacentFilter?.edgeFilter?.edgeTypeId);
                            if (linkRes.err) {
                                const { message, zombieA, zombieB } = linkRes.err;
                                res.err = new Error(`Err linking ${zombieA.name} to ${zombieB.name}: ${message} `);
                            }
                            if (linkRes.data) {
                                const [source, edge, target] = linkRes.data;
                                if (edge) {
                                    /** add the edge between exiting context object and user  */
                                    objectsToAddToState.push(edge);
                                }
                            }
                        }
                        else {
                            const msg = 'No context object (normally group) present, not linking';
                            debug && console.log(msg);
                            res.err = new Error(msg);
                        }
                    }
                    else {
                        debug && console.log('WARN saveUser no data, no err', response);
                    }
                    if (objectsToAddToState.length > 0) {
                        userFunctions.addObjectsToState(objectsToAddToState);
                    }
                    if (response.err) {
                        displayMessage(response.err.message, 'MessageBar', userFunctions, Severity.error);
                    }
                    else {
                        displayMessage(`Added ${user?.name} to userpool`, 'SnackBar', userFunctions, Severity.success);
                    }
                }
                else {
                    displayMessage('User missing password ', 'MessageBar', userFunctions, Severity.warning);
                }
            }
            else {
                displayMessage('Not a User ', 'MessageBar', userFunctions, Severity.warning);
            }
        }
    }
    catch (e) {
        console.log(`Err, ExtendedTreeItemWithContextMenu saveUser`, {
            contextCustomer,
            contextUser,
            newUserObject,
            addToGroups,
            contextObject,
        });
        throw e;
    }
    return res;
};
export const dispatchErrorMessage = (content) => {
    MainStore.dispatch(setUserMessage({
        id: v4(),
        type: 'MessageBar',
        severity: Severity.error,
        label: 'Error',
        content,
    }));
};
export const dispatchSuccessMessage = (content) => {
    MainStore.dispatch(setUserMessage({
        id: v4(),
        type: 'MessageBar',
        severity: Severity.success,
        label: 'Success',
        content,
    }));
};
/** saves and links vob (inV) to contextObject (outV) and adds to state before optionally calling postTransactionCallback with the result */
export const standardCreateAndLinkItem = async ({ contextObject, vob, edgeTypeId, postTransactionCallback }) => {
    debug && console.log('StandardContextMenuContent mutated saveObject fn was called ');
    if (contextObject && vob && edgeTypeId) {
        const res = await ConsumerDao.createAndLink({
            existingObject: contextObject,
            newObject: vob,
            direction: PathDirection.child,
            edgeTypeId,
        });
        if (res.err) {
            dispatchErrorMessage(`Context Menu Err: ${res.err.message}`);
        }
        if (res.data) {
            const [source, edge] = res.data;
            MainStore.dispatch(addObjectsToState([source, edge, vob].filter(existsFilter)));
            dispatchSuccessMessage(`Created ${vob.name ?? vob.type}`);
        }
        postTransactionCallback?.(res);
    }
    else {
        console.log(`WARN save object failing on missing data`, {
            contextObject,
            vob,
            edgeTypeId,
        });
    }
};
/** and links vobs to contextObject and adds to state before optionally calling postTransactionCallback with the result */
export const standardLinkItems = async ({ contextObject, vobs, edgeTypeId, edgeAttributes, adjacentType, postTransactionCallback }) => {
    const promises = vobs.map(async (vob) => {
        const [parentObject, childObject] = adjacentType === AdjacentType.PARENT ? [vob, contextObject] : [contextObject, vob];
        const itemLinkRes = { err: null, data: null };
        const linkRes = await AppDao.link(parentObject, childObject, edgeTypeId, edgeAttributes);
        if (linkRes.err) {
            itemLinkRes.err = { ...linkRes.err, name: 'LinkResponseError' };
        }
        if (linkRes.data) {
            const [source, edge, target] = linkRes.data;
            if (source && edge && target) {
                itemLinkRes.data = { source, edge, target };
                const postTransactionCallbackRes = postTransactionCallback?.(linkRes);
            }
            else {
                itemLinkRes.err = new Error(`Linking ${contextObject.name} to ${vob.name} failed`);
            }
        }
        return itemLinkRes;
    });
    const finalRes = ErrDataArrayReducer(await Promise.all(promises));
    if (finalRes.err) {
        dispatchErrorMessage(finalRes.err.message);
    }
    if (finalRes.data) {
        const populatedDatas = finalRes.data.filter(existsFilter);
        const message = populatedDatas.length === 1
            ? `Linked ${populatedDatas.at(0)?.source.name} to ${populatedDatas.at(0)?.target.name}`
            : `Linked ${populatedDatas.length} items`;
        dispatchSuccessMessage(message);
    }
};
export const standardDeleteItem = async ({ item, contextUser, mostProximateCustomer, UserFunctions, getConfirmation }) => {
    const res = { err: null, data: null };
    if (item?.type === UserClassName) {
        const userDeleteRes = await UserDAO.deleteUser(item);
        const { err, data } = userDeleteRes;
        if (err) {
            UserFunctions.setUserMessage({
                id: v4(),
                content: `Failed to delete ${item.name}${debug ? ` (${err.message})` : ''}`,
                label: 'System Management API Error',
                type: 'MessageBar',
                severity: Severity.error,
            });
            res.err = err;
        }
        else if (data) {
            UserFunctions.removeObjectsFromState([item]);
            UserFunctions.setUserMessage({
                id: v4(),
                content: `Deleted ${item.name}${debug ? ` (System Management API)` : ''}`,
                label: 'System Management API Error',
                type: 'MessageBar',
                severity: Severity.success,
            });
            res.data = data;
        }
        else {
            UserFunctions.setUserMessage({
                id: v4(),
                content: `Unexpexcted empty response when deleting ${item.name}${debug ? ` (System Management API)` : ''}`,
                label: 'System Management API Error',
                type: 'MessageBar',
                severity: Severity.warning,
            });
        }
    }
    else if (item?.type === DeviceClassName && mostProximateCustomer?.type === 'Customer') {
        console.log('DEVICE DELETE CONTEXT', { item, mostProximateCustomer });
        let deprovision = true;
        let deleteRes = { err: null, data: null };
        if (new Device(item).isLNC())
            deprovision = await getConfirmation({
                title: 'Deprovision from LNC?',
                question: 'This is an LNC Device, should it also be deprovisioned from LNC?',
                positive: 'Remove & Deprovision',
                alternative: 'Remove',
                neutral: 'Cancel',
            });
        if (deprovision !== null) {
            if (deprovision)
                deleteRes = await DeviceDAO.deleteDevice({
                    device: item,
                    user: contextUser,
                    contextCustomer: mostProximateCustomer,
                });
            else
                deleteRes = await ConsumerDao.deleteObject(item);
            const { err, data } = deleteRes;
            if (err) {
                UserFunctions.setUserMessage({
                    id: v4(),
                    content: `Failed to delete ${item.name}${debug ? ` (${err.message})` : ''}`,
                    label: 'System Management API Error',
                    type: 'MessageBar',
                    severity: Severity.error,
                });
                res.err = err;
            }
            if (data) {
                UserFunctions.removeObjectsFromState([item]);
                UserFunctions.setUserMessage({
                    id: v4(),
                    content: `Deleted ${item.name} ${debug ? ` (System Management API)` : ''}`,
                    label: 'System Management API Error',
                    type: 'MessageBar',
                    severity: Severity.success,
                });
                res.data = data;
            }
        }
    }
    else if (item?.type === GatewayClassName && mostProximateCustomer?.type === 'Customer') {
        console.log('GATEWAY DELETE CONTEXT', { item, mostProximateCustomer });
        const { err, data } = await GatewayDAO.deleteGateway({
            gateway: item,
            contextCustomer: mostProximateCustomer,
            contextUser: contextUser,
        });
        const name = item.name ?? `Gateway ${item.id}`;
        if (err) {
            UserFunctions.setUserMessage({
                id: v4(),
                content: `Failed to delete ${name} from ${mostProximateCustomer.name} ${debug ? ` (${err.message})` : ''}`,
                label: 'System Management API Error',
                type: 'MessageBar',
                severity: Severity.error,
            });
            res.err = err;
        }
        if (data) {
            UserFunctions.removeObjectsFromState([item]);
            UserFunctions.setUserMessage({
                id: v4(),
                content: `Deleted ${name} from ${mostProximateCustomer.name} ${debug ? ` (System Management API)` : ''}`,
                label: 'System Management API Error',
                type: 'MessageBar',
                severity: Severity.success,
            });
            res.data = data;
        }
    }
    else if (item) {
        const res = await ConsumerDao.deleteObject(item);
        const { err, data } = res;
        if (err) {
            UserFunctions.setUserMessage({
                id: v4(),
                content: `Failed to delete ${item.name}${debug ? ` (${err.message})` : ''}`,
                label: 'Consumer Management API Error',
                type: 'MessageBar',
                severity: Severity.error,
            });
            res.err = err;
        }
        if (data) {
            UserFunctions.removeObjectsFromState([item]);
            UserFunctions.setUserMessage({
                id: v4(),
                content: `Deleted ${item.name}${debug ? ` (Consumer Management API)` : ''}`,
                label: 'Consumer Management API',
                type: 'MessageBar',
                severity: Severity.success,
            });
            res.data = data;
        }
    }
    return res;
};
